import type { BlocksContent } from '@strapi/blocks-react-renderer';
import type { EventTrackingResolved } from '@/lib/gtm/pushCmsEventTracking';

/**
 * CMS config for `widgets.tutor-application-form`.
 *
 * The step labels and tracking are **flat fields** rather than nested
 * components: nested Strapi components carry a real performance cost, and this
 * widget's content changes rarely, so paying once at authoring time is the
 * better trade. That also means there is no array unwrapping to do — the raw
 * CMS shape and the resolved shape are identical.
 */
export interface TutorApplicationFormResolved {
  /** Step 1 has no back button — nothing precedes it. */
  step1CtaLabel: string;
  step1Tracking?: EventTrackingResolved[];

  step2BackLabel: string;
  step2CtaLabel: string;
  step2Tracking?: EventTrackingResolved[];

  step3BackLabel: string;
  step3CtaLabel: string;
  step3Tracking?: EventTrackingResolved[];

  step4BackLabel: string;
  step4CtaLabel: string;
  step4Tracking?: EventTrackingResolved[];

  step5BackLabel: string;
  step5CtaLabel: string;
  step5Tracking?: EventTrackingResolved[];

  step6BackLabel: string;
  step6CtaLabel: string;
  /** Consent copy containing the Datenschutzbestimmungen link. */
  step6PrivacyText: BlocksContent;
  /** Fired on submit tap, before the request completes. */
  step6SubmitTracking?: EventTrackingResolved[];
  /** Fired only after the request succeeds. */
  step6Tracking?: EventTrackingResolved[];

  confirmationBody: BlocksContent;
}

export type TutorApplicationFormRaw = TutorApplicationFormResolved;

/** Per-step labels and tracking, read off the flat config by step index. */
export interface StepChrome {
  backLabel?: string;
  ctaLabel: string;
  tracking?: EventTrackingResolved[];
}

export const stepChrome = (
  config: TutorApplicationFormResolved,
  step: number,
): StepChrome => {
  switch (step) {
    case 0:
      return { ctaLabel: config.step1CtaLabel, tracking: config.step1Tracking };
    case 1:
      return {
        backLabel: config.step2BackLabel,
        ctaLabel: config.step2CtaLabel,
        tracking: config.step2Tracking,
      };
    case 2:
      return {
        backLabel: config.step3BackLabel,
        ctaLabel: config.step3CtaLabel,
        tracking: config.step3Tracking,
      };
    case 3:
      return {
        backLabel: config.step4BackLabel,
        ctaLabel: config.step4CtaLabel,
        tracking: config.step4Tracking,
      };
    case 4:
      return {
        backLabel: config.step5BackLabel,
        ctaLabel: config.step5CtaLabel,
        tracking: config.step5Tracking,
      };
    default:
      return {
        backLabel: config.step6BackLabel,
        ctaLabel: config.step6CtaLabel,
        tracking: config.step6Tracking,
      };
  }
};
